<template>
  <div class="s-progress">
    <div
      data-test="progress-bar"
      role="progressbar"
      aria-valuemin="0"
      :aria-valuemax="max"
      :aria-valuenow="modelValue"
      :aria-label="!isScreenReaderActive && max !== 100 ? ariaLabel : undefined"
      :aria-valuetext="isScreenReaderActive ? ariaLabel : undefined"
      class="s-progress-bar"
      :class="[{ 's-progress-bar--animated': animated }, `bg-${variant}`]"
      :style="{ width: `${valueInPercentage.toFixed(2)}%` }"
    />
  </div>
</template>

<script>
export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SProgress',

  props: {
    max: {
      type: [String, Number],
      default: 100,
    },

    label: {
      type: String,
      required: true,
    },

    modelValue: {
      type: [String, Number],
      default: '',
    },

    variant: {
      type: String,
      default: 'primary',
      validator: function (value) {
        return ['primary', 'success', 'warning', 'danger'].includes(value)
      },
    },

    animated: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    isScreenReaderActive: false,
    localModelValue: 0,
  }),

  computed: {
    valueInPercentage() {
      return (this.modelValue / this.max) * 100
    },

    ariaLabel() {
      // if max is not set, its most likely a progress bar thats representing a percentage, in that case we dont need to show the steps label
      if (this.max === 100) return `${this.label}: ${Math.round(this.valueInPercentage)}%`

      return `${this.label}: ${this.$t('accessibility.progress', { current: Math.round(this.modelValue), max: this.max })}`
    },
  },

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },

    modelValue: {
      handler(newVal, oldVal) {
        if (oldVal === newVal) return

        // if value is not below maximum value, set it to max
        if (this.modelValue > this.max) {
          this.localModelValue = this.max
          return
        }

        this.localModelValue = this.modelValue
      },
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },
}
</script>

<style scoped lang="scss">
.s-progress {
  --progress-height: 4px;

  height: var(--progress-height);
  border-radius: 2px;
  background-color: $color-gray-100;
  position: relative;
  overflow: hidden;
}

.s-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  transition: width 0.6s ease;
}

.s-progress-bar--animated {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 0px,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 0px,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 0px,
    transparent
  );
  background-size: var(--progress-height) var(--progress-height);
  animation: 1s linear infinite progress-bar-stripes;

  // disable animation on reduced motion (accessibility)
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes progress-bar-stripes {
  from {
    background-position: var(--progress-height) 0;
  }
  to {
    background-position: 0 0;
  }
}
</style>
